import React, { useState, useEffect, useRef } from 'react';
import cx from 'classnames';
import FocusTrap from 'focus-trap-react';
import moment from 'moment';
import momentPropTypes from 'react-moment-proptypes';
import PropTypes from 'prop-types';

// eslint-disable-next-line import/no-cycle
import { CalendarMonthGrid } from '.';
import Button from '../Button';
import Close from '../../svg/icons/ic_close_20x20.svg';
import Icon from '../Icon';

import './DayPicker.scss';
import { getTranslation } from '../../providers/TranslationProvider';

const testId = 'day-picker';

function nextMonth(cur, i = 1) {
  return cur.clone().add(i, 'month');
}

function prevMonth(cur, i = 1) {
  return cur.clone().subtract(i, 'month');
}

const thisMonth = moment().format('YYYY-MM');

const DayPicker = ({
  className,
  clickableElementIdsToIgnore,
  closeCalendar,
  currentMonth: initialMonth,
  focusOnClose,
  numberOfMonths,
  renderDayContents,
  renderNavButton,
  showCloseButton,
  titleText,
  venueCity,
}) => {
  const [currentMonth, setCurrentMonth] = useState(initialMonth);
  const ref = useRef(null);

  const handleClickClose = (event) => {
    const calendarElement = document.getElementById('DayPicker');
    const composedPath = event.composedPath();
    if (!composedPath.includes(calendarElement) && !clickableElementIdsToIgnore.includes(event.target.id)) {
      closeCalendar();
    }
  };

  const handleKeypress = (e) => {
    if (e.key === 'Escape') {
      closeCalendar();
    }
  };

  useEffect(() => {
    window.addEventListener('click', handleClickClose);
    window.addEventListener('keydown', handleKeypress);

    return () => {
      window.removeEventListener('click', handleClickClose);
      window.removeEventListener('keydown', handleKeypress);
    };
  }, [clickableElementIdsToIgnore, closeCalendar]);

  const onNext = () => {
    setCurrentMonth(nextMonth(currentMonth));
  };

  const onPrev = () => {
    setCurrentMonth(prevMonth(currentMonth));
  };

  const isBackButtonDisabled = () => {
    if (currentMonth) {
      return moment(currentMonth).format('YYYY-MM') === thisMonth;
    }
    return true;
  };

  const isForwardButtonDisabled = () => {
    if (currentMonth) {
      const limit = numberOfMonths === 2 ? 10 : 11;
      return moment(currentMonth).format('YYYY-MM') === moment(thisMonth).add(limit, 'months').format('YYYY-MM');
    }
    return true;
  };

  return (
    <FocusTrap
      active
      focusTrapOptions={{
        allowOutsideClick: true,
        clickOutsideDeactivates: true,
        returnFocusOnDeactivate: false,
        onPostDeactivate: () => {
          document.querySelector(focusOnClose)?.focus();
        },
      }}
    >
      <div id="DayPicker" className={cx('DayPicker', className)} ref={ref}>
        <div className={cx('DayPicker__header-container', { 'DayPicker__header-container--flex-end': !titleText })}>
          {titleText}
          {(venueCity !== 'Global Nav' && showCloseButton) && (
            <div className="ResyCalendar__close">
              <Button
                variant="black-small"
                data-test-id={`${testId}-close`}
                onClick={closeCalendar}
                aria-label={getTranslation({
                  translationKey: 'close',
                  fallback: 'Close',
                })}
              >
                <Icon component={Close} name="close" />
              </Button>
            </div>
          )}
        </div>

        <div className="DayPicker__navigation">
          {isBackButtonDisabled() ? <span /> : renderNavButton({ direction: 'previous', onClick: onPrev, currentMonth })}
          {isForwardButtonDisabled() ? <span /> : renderNavButton({ direction: 'next', onClick: onNext, currentMonth })}
        </div>

        <div className="DayPicker__viewport">
          <CalendarMonthGrid
            initialMonth={currentMonth}
            numberOfMonths={numberOfMonths}
            renderDayContents={renderDayContents}
          />
        </div>
      </div>
    </FocusTrap>
  );
};

DayPicker.propTypes = {
  className: PropTypes.string,
  clickableElementIdsToIgnore: PropTypes.arrayOf(PropTypes.string),
  closeCalendar: PropTypes.func.isRequired,
  currentMonth: momentPropTypes.momentObj,
  focusOnClose: PropTypes.string.isRequired,
  numberOfMonths: PropTypes.number,
  renderDayContents: PropTypes.func,
  renderNavButton: PropTypes.func.isRequired,
  showCloseButton: PropTypes.bool,
  titleText: PropTypes.element,
  venueCity: PropTypes.string.isRequired,
};

DayPicker.defaultProps = {
  className: undefined,
  clickableElementIdsToIgnore: [],
  currentMonth: moment(),
  numberOfMonths: 1,
  renderDayContents: undefined,
  showCloseButton: true,
  titleText: null,
};

export default DayPicker;
